import React, { useEffect, useState } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { Card, CardContent } from '../ui/Card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

interface AnalysisProgressBarProps {
  sessionId: string;
  onComplete?: (analysisId: number) => void;
  onError?: (error: string) => void;
}

interface ProgressState {
  stage: string;
  progress: number;
  message: string;
  currentSection?: number;
  totalSections?: number;
  status: 'analyzing' | 'completed' | 'error';
  error?: string;
}

export const AnalysisProgressBar: React.FC<AnalysisProgressBarProps> = ({
  sessionId,
  onComplete,
  onError,
}) => {
  const { onProgressUpdate, onAnalysisCompleted, onAnalysisError } = useSocket();
  const [progressState, setProgressState] = useState<ProgressState>({
    stage: 'starting',
    progress: 0,
    message: 'Iniciando análisis...',
    status: 'analyzing',
  });

  useEffect(() => {
    // Listen for progress updates
    const unsubscribeProgress = onProgressUpdate((update) => {
      if (update.sessionId === sessionId) {
        setProgressState({
          stage: update.stage,
          progress: update.progress,
          message: update.message,
          currentSection: update.currentSection,
          totalSections: update.totalSections,
          status: 'analyzing',
        });
      }
    });

    // Listen for completion
    const unsubscribeCompleted = onAnalysisCompleted((data) => {
      if (data.sessionId === sessionId) {
        setProgressState((prev) => ({
          ...prev,
          progress: 100,
          status: 'completed',
          message: 'Análisis completado',
        }));

        if (onComplete) {
          onComplete(data.analysisId);
        }
      }
    });

    // Listen for errors
    const unsubscribeError = onAnalysisError((data) => {
      if (data.sessionId === sessionId) {
        setProgressState((prev) => ({
          ...prev,
          status: 'error',
          error: data.error,
          message: `Error: ${data.error}`,
        }));

        if (onError) {
          onError(data.error);
        }
      }
    });

    return () => {
      unsubscribeProgress();
      unsubscribeCompleted();
      unsubscribeError();
    };
  }, [sessionId, onProgressUpdate, onAnalysisCompleted, onAnalysisError, onComplete, onError]);

  const getStageLabel = (stage: string): string => {
    const stageLabels: Record<string, string> = {
      starting: 'Iniciando',
      parsing: 'Procesando documento',
      analyzing: 'Analizando contenido',
      saving: 'Guardando resultados',
      completed: 'Completado',
    };
    return stageLabels[stage] || stage;
  };

  const getProgressColor = (): string => {
    if (progressState.status === 'error') return 'bg-red-500';
    if (progressState.status === 'completed') return 'bg-green-500';
    return 'bg-blue-500';
  };

  const getIconColor = (): string => {
    if (progressState.status === 'error') return 'text-red-500';
    if (progressState.status === 'completed') return 'text-green-500';
    return 'text-blue-500';
  };

  const renderIcon = () => {
    if (progressState.status === 'error') {
      return <FontAwesomeIcon icon={faExclamationTriangle} className={`text-2xl ${getIconColor()}`} />;
    }
    if (progressState.status === 'completed') {
      return <FontAwesomeIcon icon={faCheckCircle} className={`text-2xl ${getIconColor()}`} />;
    }
    return <FontAwesomeIcon icon={faSpinner} spin className={`text-2xl ${getIconColor()}`} />;
  };

  return (
    <Card className="w-full">
      <CardContent className="p-6">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center gap-4">
            {renderIcon()}
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-800">
                {getStageLabel(progressState.stage)}
              </h3>
              <p className="text-sm text-gray-600">{progressState.message}</p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-gray-800">{progressState.progress}%</span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative h-4 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className={`h-full transition-all duration-300 ease-out ${getProgressColor()}`}
              style={{ width: `${progressState.progress}%` }}
            >
              <div className="h-full w-full animate-pulse bg-white/20"></div>
            </div>
          </div>

          {/* Section Progress */}
          {progressState.currentSection && progressState.totalSections && (
            <div className="text-sm text-gray-600">
              Sección {progressState.currentSection} de {progressState.totalSections}
            </div>
          )}

          {/* Error Message */}
          {progressState.status === 'error' && progressState.error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-800">{progressState.error}</p>
            </div>
          )}

          {/* Success Message */}
          {progressState.status === 'completed' && (
            <div className="rounded-md bg-green-50 border border-green-200 p-3">
              <p className="text-sm text-green-800">
                El análisis se ha completado exitosamente.
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
